<template>
  <div class="user-credit-cards">
    <a-row type="flex" justify="space-between">
      <a-col :lg="10" :md="10" :xs="24">
        <h2 class="list-title">
          <a-icon type="folder" class="list-icon" />
          <span>{{ labels.title }}</span>
        </h2>
        <p class="list-subtitle">{{ labels.subtitle }}</p>
      </a-col>
      <a-col :lg="14" :md="14" :xs="24">
        <a-row
          type="flex"
          justify="end"
          :gutter="[8, 8]"
          :style="{
            alignItems: 'flex-end',
            marginTop: '25px',
            marginBottom: '10px',
            textAlign: 'right',
          }"
        >
          <a-col :xs="8" :sm="4" :md="4" :style="{ textAlign: 'right' }">
            <a-button type="primary" icon="plus" @click="handleOpenModal">
              {{ labels.add }}
            </a-button>
          </a-col>
        </a-row>
      </a-col>
    </a-row>
    <a-row>
      <a-col :span="24">
        <StandardTable
          ref="standardTable"
          :loading="loading"
          :data="data"
          :columns="columns"
          :locale="locale"
          :handleShowTotal="false"
          :scroll="{ x: true }"
          :paginate="false"
          :customRow="handleCustomRow"
          :rowKey="(record) => record.card_number"
        />
      </a-col>
    </a-row>
    <a-modal
      v-model="openModal"
      :title="form.index !== null ? labels.modal.edit : labels.modal.add"
      :okText="form.id ? labels.modal.updateText : labels.modal.okText"
      :cancelText="labels.modal.cancelText"
      @ok="handleSave"
      @cancel="handleCancel"
      :width="this.screenWidth >= 768 ? 720 : '100%'"
    >
      <ValidationObserver v-if="openModal" ref="creditCardObserver">
        <a-row type="flex" justify="start" :gutter="[16, 16]">
          <a-col :xs="24" :sm="24" :md="12">
            <SelectPagination
              v-model="form.country"
              :data="countriesOptions"
              :placeholder="labels.form.country.placeholder"
              :labelKey="'label'"
              :valueKey="'value'"
              :label="labels.form.country.label"
              rules="required"
              :customStyle="{ width: '100%' }"
            />
          </a-col>
          <a-col :xs="24" :sm="24" :md="12">
            <SwitchInput
              v-model.lazy="form.is_verified"
              :label="labels.form.isVerified"
              :align="'right'"
            />
          </a-col>
          <a-col :xs="24" :sm="24" :md="12">
            <Input
              v-model="form.card_number"
              :label="labels.form.cardNumber.label"
              :placeholder="labels.form.cardNumber.placeholder"
              :rules="'required'"
              :allowClear="true"
              :showLabel="false"
              :maxLength="4"
              v-mask="'####'"
            />
          </a-col>
          <a-col :xs="24" :sm="24" :md="12">
            <Input
              v-model="form.card_expiration_date"
              :label="labels.form.cardExpirationDate.label"
              :placeholder="labels.form.cardExpirationDate.placeholder"
              :rules="'required|creditCardExpirationDate'"
              :allowClear="true"
              :showLabel="false"
              v-mask="'##/##'"
            />
          </a-col>
          <a-col :span="24">
            <a-form-item
              :style="{ width: '100%' }"
              :label="labels.form.image.label"
            >
              <a-upload
                :multiple="false"
                :accept="'image/*'"
                :file-list="imageFileList"
                list-type="picture"
                :style="{ width: '100%' }"
                :beforeUpload="beforeUploadImage"
                :openFileDialogOnClick="true"
                :remove="handleRemoveImageFile"
                @preview="handlePreviewFile"
                :customRequest="dummyRequest"
              >
                <a-button
                  size="large"
                  type="primary"
                  ghost
                  class="btn-upload"
                  block
                  :style="{ width: '100%' }"
                >
                  <a-icon type="upload" />
                  {{ labels.form.image.placeholder }}
                </a-button>
              </a-upload>
            </a-form-item>
          </a-col>
        </a-row>
      </ValidationObserver>
    </a-modal>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import { ValidationObserver } from "vee-validate";
import StandardTable from "@/components/core/table/StandardTable.vue";
import SelectPagination from "@/components/core/SelectPagination/index.vue";
import Input from "@/components/core/VeeValidateForm/Input.vue";
import SwitchInput from "@/components/core/VeeValidateForm/Switch.vue";
import { toBase64 } from "../../../utils/utils";

const defaultForm = {
  index: null,
  id: null,
  country: null,
  is_verified: false,
  card_number: "",
  card_expiration_date: "",
  image: null,
};

export default {
  name: "CreditCards",
  components: {
    StandardTable,
    ValidationObserver,
    SelectPagination,
    Input,
    SwitchInput,
  },
  props: {
    labels: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dataSource: {
      type: Array,
      required: true,
      default: () => [],
    },
    onlyUnassignedTo: {
      type: Number,
      default: null,
    },
  },
  emits: ["update:modelValue", "change", "delete"],
  data() {
    return {
      data: [],
      form: _.cloneDeep(defaultForm),
      openModal: false,
      activeRow: null,
      imageFileList: [],
    };
  },
  filters: {
    ucwords(value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    ...mapState("systemConfig", ["countries"]),
    isMobile() {
      return this.screenWidth < 600;
    },
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.cardNumber,
          dataIndex: "card_number",
          key: "card_number",
          width: "15%",
          hidden: this.isMobile,
          customRender: (text) => {
            return text || "";
          },
        },
        {
          title: this.labels.table.columns.cardExpirationDate,
          dataIndex: "card_expiration_date",
          key: "card_expiration_date",
          width: "15%",
          hidden: this.isMobile,
          customRender: (text) => {
            return text || "";
          },
        },
        {
          title: this.labels.table.columns.country,
          dataIndex: "country",
          key: "country",
          width: "18%",
          hidden: this.isMobile,
          customRender: (text) => {
            return text?.value || "";
          },
        },
        {
          title: this.labels.table.columns.isVerified.title,
          dataIndex: "is_verified",
          key: "is_verified",
          width: "10%",
          align: "center",
          hidden: this.isMobile,
          customRender: (text) => {
            return (
              <a-tag color={text ? "green" : "red"}>
                {text
                  ? this.labels.table.columns.isVerified.yes
                  : this.labels.table.columns.isVerified.no}
              </a-tag>
            );
          },
        },
        {
          title: this.labels.table.columns.verifyBy,
          dataIndex: "verified_by",
          key: "verified_by",
          width: "15%",
          align: "center",
          hidden: this.isMobile,
          customRender: (text) => {
            return text?.name || "-";
          },
        },
        {
          title: this.labels.table.columns.verifyAt,
          dataIndex: "verified_at",
          key: "verified_at",
          width: "13%",
          align: "center",
          hidden: this.isMobile,
          customRender: (text) => {
            return text
              ? this.$moment(text).format("DD/MM/YYYY H:mm:ss A")
              : "-";
          },
        },
        {
          title: this.labels.table.columns.actions.title,
          dataIndex: "actions",
          key: "actions",
          width: "12%",
          align: "center",
          hidden: this.isMobile,
          customRender: (_, record, index) => {
            return (
              <div class="action-buttons">
                <a-button
                  type="link"
                  icon="edit"
                  class="action-button-edit"
                  title={this.labels.table.columns.actions.edit}
                  onClick={() => this.handleEdit(record, index)}
                />
                <a-button
                  type="link"
                  icon="delete"
                  class="action-button-delete"
                  title={this.labels.table.columns.actions.delete}
                  onClick={() =>
                    record?.id
                      ? this.confirmDelete(index)
                      : this.handleDelete(index)
                  }
                />
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.creditCards,
          dataIndex: "credit_cards",
          key: "credit_cards",
          width: "100%",
          hidden: !this.isMobile,
          customRender: (_, record, index) => {
            // Card item design
            const color = record?.is_verified ? "#108ee9" : "#ff5500";
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>{record.card_number}</span>
                    <small>{record.card_expiration_date}</small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-lottery">
                      <span>{record.country?.value || record.country}</span>
                    </div>
                    <div class="list-item-head-content-status">
                      <a-tag color={color}>
                        {record?.is_verified
                          ? this.labels.table.columns.isVerified.verified
                          : this.labels.table.columns.isVerified.unverified}
                      </a-tag>
                    </div>
                  </div>
                </div>
                {this.activeRow === index && (
                  <div class="list-item-actions">
                    <a-button-group>
                      <a-button
                        class="edit-button ant-btn ant-btn-primary"
                        title={this.labels.table.columns.actions.edit}
                        onClick={() => this.handleEdit(record, index)}
                        type="primary"
                      >
                        {this.labels.table.columns.actions.edit}
                      </a-button>
                      <a-button
                        class="delete-button ant-btn ant-btn-danger"
                        title={this.labels.table.columns.actions.delete}
                        onClick={() =>
                          record?.id
                            ? this.confirmDelete(index)
                            : this.handleDelete(index)
                        }
                        type="danger"
                      >
                        {this.labels.table.columns.actions.delete}
                      </a-button>
                    </a-button-group>
                  </div>
                )}
              </div>
            );
          },
        },
      ].filter((column) => !column.hidden);
    },
    countriesOptions() {
      return this.countries.map((country) => ({
        label: country,
        value: country,
      }));
    },
  },
  methods: {
    ...mapActions("lotteries", ["fetchLotteries"]),
    async beforeUploadImage(file) {
      const fileBase64 = await toBase64(file);
      this.imageFileList = [
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          url: fileBase64,
          thumbUrl: fileBase64,
        },
      ];
      this.form.image = file;
      return true;
    },
    handleRemoveImageFile(file) {
      if (file?.uid) {
        this.form.image = null;
        this.imageFileList = [];
      }
    },
    handlePreviewFile(file) {
      // CREATE IMG AND OPEN IN NEW TAB
      const img = document.createElement("img");
      img.src = file.url || file.thumbUrl;
      img.style.maxWidth = "520px";
      const w = window.open("about:blank", "_blank", "width=520,height=auto");
      w?.document.write(img.outerHTML);
    },
    dummyRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
    resetForm() {
      this.form = _.cloneDeep(defaultForm);
      this.imageFileList = [];
      this.$refs.creditCardObserver.reset();
    },
    handleEdit(record, index) {
      this.form = _.cloneDeep({ ...record, index });
      this.imageFileList = record.image
        ? [
            {
              uid: record.id,
              name: record.image,
              status: "done",
              url: record.image,
              thumbUrl: record.image,
            },
          ]
        : [];
      this.openModal = true;
    },
    confirmDelete(index) {
      this.$confirm({
        title: this.labels.table.confirmDelete.title,
        content: this.labels.table.confirmDelete.content,
        okText: this.labels.table.confirmDelete.okText,
        okType: "danger",
        cancelText: this.labels.table.confirmDelete.cancelText,
        onOk: () => {
          this.handleDelete(index);
        },
      });
    },
    async handleSave() {
      try {
        const valid = await this.$refs.creditCardObserver.validate();
        if (!valid) return;

        if (this.form.index !== null) {
          this.data[this.form.index] = _.cloneDeep(this.form);
        } else {
          this.data.push(_.cloneDeep(this.form));
        }
        this.handleUpdate();
        this.openModal = false;
        this.resetForm();
      } catch (error) {
        console.error(error);
      }
    },
    handleCancel() {
      this.resetForm();
      this.openModal = false;
    },
    handleOpenModal() {
      this.openModal = true;
    },
    handleDelete(index) {
      const found = _.cloneDeep(this.data[index]);
      this.$emit("delete", { index, record: found });
      // REMOVE FROM DATA
      this.data.splice(index, 1);
      this.handleUpdate();
    },
    handleUpdate() {
      const data = _.cloneDeep(this.data).map((item) => ({
        ...item,
        country: item.country?.value || item.country,
      }));
      this.$emit("update:modelValue", data);
      this.$emit("change", data);
    },
  },
  watch: {
    dataSource: {
      handler() {
        if (this.dataSource.length === 0) this.data = [];
        else
          this.data = _.cloneDeep(this.dataSource).map((item) => ({
            ...item,
            ...(item.country && {
              country: this.countriesOptions.find((country) =>
                country.value === item.country?.value
                  ? item.country.value
                  : item.country
              ),
            }),
            is_verified: !!item.is_verified,
          }));
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/variable";
.user-credit-cards {
  .list {
    &-title {
      margin-bottom: 10px;
      .list-icon {
        margin-right: 10px;
      }
    }
    &-subtitle {
      margin-bottom: 10px;
    }
  }
  .add-button {
    color: color(primary);
    border-color: color(primary);
    margin-left: 10px;
  }
  .action-buttons {
    display: flex;
    justify-content: center;
    gap: 10px;
    .action-button-view {
      color: color(primary);
    }
    .action-button-cancel {
      color: color(danger);
    }
  }

  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 22%;
        min-width: 22%;
        border-right: 4px solid color(border);
        .success {
          color: color(success);
        }
        .warning {
          color: color(warning);
        }
        .danger {
          color: color(danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 78%;
        min-width: 78%;
        overflow: hidden;
        &-lottery {
          span {
            font-size: 14px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
      }
    }
    &-actions {
      width: 100%;
      margin-top: 10px;
      .ant-btn-group {
        width: 100%;
        .more-info-button,
        .delete-button,
        .edit-button {
          width: 100%;
        }
      }
    }
  }
}
</style>
<style lang="scss">
.ant-upload {
  width: 100%;
}
.confirm-preview {
  display: flex;
  width: 100%;
  flex-direction: column;
  .confirm-preview-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    .confirm-preview-content-item {
      display: flex;
      flex-direction: row;
      gap: 10px;
      .confirm-preview-content-item-label {
        font-weight: 600;
        &:after {
          content: ":";
        }
      }
      .confirm-preview-content-item-value {
        font-weight: 400;
        &.money {
          font-weight: 500;
        }
      }
    }
  }
}
</style>
